export const TailwindRACQTheme = {
  // Global styles apply to _all_ inputs with matching section keys
  global: {
    fieldset: 'flex flex-row rounded mx-0',
    help: 'text-xs text-dark-grey-text text-block',
    inner: 'formkit-disabled:cursor-not-allowed formkit-disabled:pointer-events-none relative',
    input: 'appearance-none bg-transparent focus:outline-none focus:ring-0 focus:shadow-none',
    label: 'block text-base-bold text-racq-dark-grey mb-1 formkit-label block text-input-label w-auto mb-1 sm:pe-8',
    legend: 'text-sm-bold formkit-label block text-input-label w-auto mb-1 sm:pe-8',
    loaderIcon: 'inline-flex items-center w-4 text-racq-dark-grey animate-spin',
    message: 'text-racq-error-red mb-1 text-xs mt-1',
    messages: 'list-none p-0 mt-1 mb-0',
    outer: 'formkit-disabled:opacity-50',
    prefixIcon:
      'w-10 flex self-stretch grow-0 shrink-0 rounded-tl rounded-bl border-r border-gray-400 bg-white bg-gradient-to-b from-transparent to-gray-200 [&>svg]:w-full [&>svg]:max-w-[1em] [&>svg]:max-h-[1em] [&>svg]:m-auto',
    suffixIcon:
      'w-7 flex self-stretch grow-0 shrink-0 [&>svg]:w-full [&>svg]:max-w-[1em] [&>svg]:max-h-[1em] [&>svg]:m-auto',
  },

  // Family styles apply to all inputs that share a common family
  'family:box': {
    decorator: `block relative h-6 w-6 md:h-5 md:w-5 mr-3 rounded-sm  from-transparent to-gray-200 border border-racq-blue-border shadow-md bg-white
      peer-checked:bg-racq-blue-active peer-checked:border-racq-blue-active peer-checked:ring-racq-blue-active text-transparent peer-checked:text-white
      peer-focus-visible:outline-2 peer-focus-visible:outline-dashed peer-focus-visible:outline-racq-navy peer-focus-visible:outline-offset-2`,
    decoratorIcon:
      'flex p-[4px] md:p-[3px] w-full h-full rounded-sm  absolute top-1/2 left-1/2 -translate-y-1/2 -translate-x-1/2',
    help: '',
    input: 'absolute w-0 h-0 overflow-hidden opacity-0 pointer-events-none peer ',
    inner: '',
    outer: '',
    label: '$reset text-sm-bold text-gray-700 select-none',
    wrapper: 'flex items-center',
  },

  'family:button': {
    input:
      '$reset inline-flex items-center text-[1.125rem] font-[600] rounded focus-visible:outline-2 focus-visible:outline-blue-600 focus-visible:outline-offset-2 text-base-bold',
    prefixIcon: '$reset block w-4 -ml-2 mr-2 stretch',
    suffixIcon: '$reset block w-4 ml-2 stretch',
    outer: '$reset formkit-disabled:opacity-50',
  },

  'family:dropdown': {
    help: 'mt-1',
    dropdownWrapper: 'mt-1 ml-[-12px] md:ml-[-1rem] w-full shadow-1xl rounded bg-white',
    emptyMessageInner:
      'flex items-center justify-center text-sm p-2 text-center w-full text-racq-light-grey [&>span]:mr-3 [&>span]:ml-0',
    inner:
      'w-full p-3 md:px-4 md:py-2 relative flex border border-racq-blue-border shadow-md focus-within:outline-racq-blue-active focus-within:outline focus-within:outline-2 focus-within:outline-offset-[-2px] rounded mb-0 md:max-h-[40px] formkit-disabled:bg-racq-pale-grey formkit-disabled:text-racq-anti-flash-white',
    input: 'w-full text-base text-racq-dark-grey',
    listbox: 'list-none overflow-hidden mb-0 p-1',
    listboxButton:
      '[&_svg]:aria-expanded:transform [&_svg]:aria-expanded:rotate-180 [&_svg_path]:formkit-disabled:stroke-[#B1B1B1] py-1',
    listitem:
      'relative hover:bg-racq-anti-flash-white data-[is-active="true"]:bg-racq-anti-flash-white cursor-pointer rounded',
    loaderIcon: 'ml-auto',
    loadMoreInner:
      'flex items-center justify-center text-sm p-2 text-center w-full text-racq-blue-active formkit-loading:text-racq-light-grey cursor-pointer [&>span]:mr-3 [&>span]:ml-0',
    option: 'p-3 text-racq-dark-grey text-m',
    optionLoading: 'p-3 text-racq-light-grey',
    placeholder: 'text-racq-grey text-base whitespace-nowrap',
    selector:
      'flex w-full justify-between items-center cursor-default outline-none [&_svg]:aria-expanded:transform [&_svg]:aria-expanded:rotate-180 [&_svg_path]:formkit-disabled:stroke-[#B1B1B1] gap-2',
    selectionWrapper: 'w-full text-left',
    selection: '*:!text-base *:line-clamp-1 *:p-0',
    selectedIcon: 'hidden',
    selectIcon: ' w-4 h-4',
  },

  'family:text': {
    inner:
      'flex items-center outline outline-1 outline-racq-blue-border shadow-md focus-within:border-transparent focus-within:ring-racq-blue-active focus-within:ring-2 [&>label:first-child]:focus-within:text-racq-blue-active rounded mb-1 bg-racq-true-white formkit-disabled:bg-racq-pale-grey formkit-disabled:text-racq-anti-flash-white',
    input: 'w-full px-3 py-2 border-none text-base text-gray-700 placeholder-gray-400',
  },

  'family:date': {
    inner:
      'flex items-center max-w-60 border border-racq-blue-border shadow-md focus-within:ring-racq-blue-active focus-within:ring-2 [&>label:first-child]:focus-within:text-racq-blue-active rounded mb-1 formkit-disabled:bg-racq-pale-grey formkit-disabled:text-racq-anti-flash-white',
    input: 'w-full px-3 py-2 border-none text-gray-700 placeholder-gray-400',
  },

  // Specific styles apply only to a given input type
  checkbox: {
    help: '',
    inner: 'formkit-disabled:opacity-50',
    options: 'flex flex-col list-none pl-0 gap-4 mb-2 mt-4',
    option: 'flex flex-col',
    fieldset: '$reset flex flex-col',
    legend: '$reset text-sm-bold formkit-label block text-input-label w-auto sm:pe-8',
    optionHelp: 'text-xs text-racq-med-grey ml-[33px] mt-0.5',
    label: '$reset text-base text-racq-dark-grey',
  },
  file: {
    fileItem: 'flex items-center text-gray-800 mb-1 last:mb-0',
    fileItemIcon: 'w-4 mr-2 shrink-0',
    fileList: 'shrink grow peer px-3 py-2 formkit-multiple:data-[has-multiple="true"]:mb-6',
    fileName: 'break-all grow text-ellipsis',
    fileRemove:
      'relative z-[2] ml-auto text-[0px] hover:text-red-500 pl-2 peer-data-[has-multiple=true]:text-sm peer-data-[has-multiple=true]:text-racq-blue-active peer-data-[has-multiple=true]:ml-3 peer-data-[has-multiple=true]:mb-2 formkit-multiple:bottom-[0.15em] formkit-multiple:pl-0 formkit-multiple:ml-0 formkit-multiple:left-[1em] formkit-multiple:formkit-prefix-icon:left-[3.75em]',
    fileRemoveIcon: 'block text-base w-3 relative z-[2]',
    inner: 'relative max-w-md cursor-pointer formkit-multiple:[&>button]:absolute',
    input: 'cursor-pointer text-transparent absolute top-0 right-0 left-0 bottom-0 opacity-0 z-[2]',
    noFiles: 'flex w-full items-center px-3 py-2 text-gray-400',
    noFilesIcon: 'w-4 mr-2',
  },
  text: {
    inner: 'relative',
  },
  radio: {
    wrapper: '',
    outer: '',
    options: 'racq-radio',
    decorator: 'hidden',
    fieldset: 'flex-col',
    legend: 'text-input-label formkit-label block text-input-label w-auto mb-3 sm:pe-0',
    label: 'formkit-label',
  },
  range: {
    inner: '$reset flex items-center max-w-md',
    input: '$reset w-full mb-1 h-2 p-0 rounded-full',
    prefixIcon:
      '$reset w-4 mr-1 flex self-stretch grow-0 shrink-0 [&>svg]:max-w-[1em] [&>svg]:max-h-[1em] [&>svg]:m-auto',
    suffixIcon:
      '$reset w-4 ml-1 flex self-stretch grow-0 shrink-0 [&>svg]:max-w-[1em] [&>svg]:max-h-[1em] [&>svg]:m-auto',
  },
  select: {
    label: 'text-sm-bold w-[180px] mr-[20px]',
    inner:
      'flex relative max-w-md items-center rounded mb-1 border border-racq-blue-border shadow-md focus-within:ring-racq-blue-active focus-within:ring-2 [&>span:first-child]:focus-within:text-racq-blue-active input-md',
    input:
      'w-full pl-3 pr-8 py-2 border-none text-base text-gray-700 placeholder-gray-400 formkit-multiple:p-0 data-[placeholder="true"]:text-gray-400 formkit-multiple:data-[placeholder="true"]:text-inherit',
    selectIcon: 'flex p-[3px] shrink-0 w-5 mr-2 -ml-[1.5em] h-full pointer-events-none [&>svg]:w-[1em]',
    option: 'formkit-multiple:p-3 formkit-multiple:text-sm text-gray-700',
  },
  textarea: {
    wrapper: 'md:flex',
    label: 'text-sm-bold w-[180px] mr-[20px]',
    inner:
      'flex rounded mb-1 border border-racq-blue-border shadow-md focus-within:ring-racq-blue-active [&>label:first-child]:focus-within:text-racq-blue-active focus-within:ring-2',
    input:
      'block w-full md:min-w-[330px] h-32 min-h-[150px] px-3 py-3 border-none text-base text-gray-700 placeholder-gray-400 focus:shadow-outline',
  },

  // ----------------------------------------
  // PRO input styles
  // ----------------------------------------

  datepicker: {
    inner: 'relative',
    panelWrapper:
      'absolute top-[calc(100%_+_0.5em)] bg-white rounded shadow-1xl flex-col gap-4 p-4 inline-flex min-w-[320px] z-[110]',
    panelHeader: 'p-2 bg-white border-b border-neutral-200 items-center gap-2 flex',
    input: 'selection:bg-racq-blue-active/70',
    monthsHeader: 'grow shrink basis-0 justify-center items-center gap-3 flex',
    timeHeader: 'grow shrink basis-0 justify-center items-center gap-3 flex',
    overlayPlaceholder: 'text-gray-400',
    months: 'flex flex-wrap',
    month: `
      flex items-center justify-center cursor-pointer
      w-12 m-1 p-2 h-8 rounded
      text-m text-racq-dark-grey bg-racq-anti-flash-white
      aria-selected:bg-racq-blue-active aria-selected:text-white
      focus:outline focus:outline-2 focus:outline-racq-blue-active focus:bg-white
      data-[is-extra=true]:opacity-25
      formkit-disabled:opacity-50 formkit-disabled:cursor-default formkit-disabled:pointer-events-none
    `,
    yearsHeader: 'grow shrink basis-0 justify-center items-center gap-3 flex text-sm',
    years: 'flex flex-wrap',
    year: `
      flex items-center justify-center cursor-pointer
      w-12 m-1 p-2 h-8 rounded
      text-m text-racq-dark-grey bg-racq-anti-flash-white
      aria-selected:bg-racq-blue-active aria-selected:text-white
      focus:outline focus:outline-2 focus:outline-racq-blue-active focus:bg-white
      data-[is-extra=true]:opacity-25
      formkit-disabled:opacity-50 formkit-disabled:cursor-default formkit-disabled:pointer-events-none
    `,
    weekDays: 'flex',
    weekDay: 'flex w-8 h-8 m-1 items-center justify-center rounded text-sm uppercase',
    week: 'flex formkit-disabled:opacity-50 formkit-disabled:cursor-default formkit-disabled:pointer-events-none',
    dayCell: `
      flex items-center justify-center cursor-pointer
      w-8 h-8 m-1 p-2 rounded
      text-m text-racq-dark-grey bg-racq-anti-flash-white
      aria-selected:bg-racq-blue-active aria-selected:text-white
      focus:outline focus:outline-2 focus:outline-racq-blue-active focus:bg-white
      formkit-disabled:opacity-50 formkit-disabled:cursor-default formkit-disabled:pointer-events-none
    `,
    timeInput: 'w-full border-2 border-gray-300 rounded p-2 my-[2em] focus-visible:outline-racq-blue-active',
    daysHeader: 'grow shrink basis-0 justify-center items-center gap-3 flex',
    prev: 'w-4 h-4 relative',
    prevLabel: 'hidden',
    prevIcon: 'flex w-4 h-4 select-none [&>svg]:w-full',
    dayButton: 'appearance-none cursor-pointer px-3 py-1 border-2 rounded mx-1 hover:bg-racq-anti-flash-white',
    monthButton: 'appearance-none text-sm text-racq-dark-grey rounded hover:bg-racq-anti-flash-white',
    yearButton: 'appearance-none text-sm text-racq-dark-grey rounded hover:bg-racq-anti-flash-white',
    next: 'w-4 h-4 relative',
    nextLabel: 'hidden',
    nextIcon: 'flex w-4 h-4 select-none [&>svg]:w-full',
    openButton: `
      appearance-none border-0 bg-transparent flex p-0 mr-3 self-stretch cursor-pointer w-6 h-6
      focus-visible:outline-none focus-visible:text-white focus-visible:bg-racq-blue-active
    `,
    calendarIcon: 'flex grow-0 shrink-0 self-stretch select-none',
  },
  dropdown: {
    tagsWrapper: 'max-w-[calc(100%_-_35px)]',
    tags: 'flex items-center flex-wrap gap-1 mx-2 my-1.5',
    tag: 'flex items-center rounded-full bg-gray-200 text-xs text-black py-1 px-2.5 cursor-default',
    tagLabel: 'px-1',
    selectionsWrapper: 'flex w-[calc(100%_-_35px)] overflow-hidden',
    selections: 'inline-flex items-center px-2.5',
    selectionsItem: 'whitespace-nowrap mr-1 last:mr-0',
    truncationCount:
      'flex items-center whitespace-nowrap justify-center rounded text-white bg-gray-500 text-xs-bold px-1 py-0.5',
    removeSelection: 'block w-2.5 my-1 cursor-pointer',
    option: 'text-black',
  },
  taglist: {
    input: 'px-1 py-1 w-[0%] grow',
    removeSelection: 'w-2.5 mx-1 self-center text-black leading-none',
    tag: 'flex items-center my-1 p-1 bg-gray-200 text-xs rounded-full',
    tagWrapper:
      'mr-1 focus:outline-none focus:text-white [&>div]:focus:bg-racq-blue-active [&>div>button]:focus:text-white',
    tagLabel: 'pl-2 pr-1',
    tags: 'flex items-center flex-wrap w-full py-1.5 px-2',
  },
  toggle: {
    altLabel: 'block w-full mb-1 text-sm-bold',
    inner: '$reset',
    input: 'peer absolute opacity-0 pointer-events-none',
    innerLabel: 'text-[10px] font-bold absolute left-full top-1/2 -translate-x-full -translate-y-1/2 px-1',
    thumb: 'relative left-0 aspect-square rounded-full transition-all w-5 bg-gray-100',
    track:
      'p-0.5 min-w-[3em] relative rounded-full transition-all bg-gray-400 peer-checked:bg-racq-blue-active peer-checked:[&>div:last-child]:left-full peer-checked:[&>div:last-child]:-translate-x-full peer-checked:[&>div:first-child:not(:last-child)]:left-0 peer-checked:[&>div:first-child:not(:last-child)]:translate-x-0',
    valueLabel: 'text-sm-bold',
    wrapper: 'items-center',
    label: 'mb-3',
    help: 'mt-2',
  },
  nativeToggle: {
    // label above switch
    altLabel: 'block w-full mb-1 text-xs-bold',
    // wrapper around track & thumb
    wrapper: 'flex items-center',
    // the hidden <input type="checkbox">
    input: 'peer absolute opacity-0 pointer-events-none',
    // the “track” background
    track:
      'p-0.5 min-w-[3em] relative rounded-full transition-all bg-racq-light-blue-grey ' +
      'peer-checked:bg-racq-blue-active ' +
      'peer-checked:[&>div:last-child]:left-full ' +
      'peer-checked:[&>div:last-child]:-translate-x-full ' +
      'peer-checked:[&>div:first-child:not(:last-child)]:left-0 ' +
      'peer-checked:[&>div:first-child:not(:last-child)]:translate-x-0',
    // the moving thumb
    thumb: 'relative left-0 aspect-square rounded-full transition-all w-5 bg-white',
    // label inside the track (“On” / “Off”)
    innerLabel: 'text-[10px] font-bold absolute left-full top-1/2 ' + '-translate-x-full -translate-y-1/2 px-1',
    // extra inner reset styling if needed
    inner: '$reset',
    // under-track value label
    valueLabel: 'text-sm font-bold',
    // margin under the entire control
    label: '$reset ml-2 text-xs-bold',
    // helper text below
    help: 'mt-2 text-sm text-gray-500',
  },
  togglebuttons: {
    inputInner: 'flex flex-col w-full h-full',
    input: `
      $reset
      w-full
      h-full
      border
      border-racq-light-blue-grey
      rounded
      shadow-md
      bg-white
      aria-pressed:border-2
      aria-pressed:border-racq-blue-active
      aria-pressed:bg-racq-anti-flash-white
      enabled:hover:bg-racq-pale-blue enabled:hover:border-racq-cool-grey
      disabled:cursor-not-allowed
      disabled:opacity-50
      focus-visible:outline-2 focus-visible:outline-dashed focus-visible:outline-racq-navy focus-visible:outline-offset-1
    `,
    options: 'flex flex-col gap-4 mt-4',
    option: 'basis-0 grow shrink',
  },
  slider: {
    outer: 'w-full',
    wrapper: 'my-[11px]',
    tooltipMax: 'hidden',
    trackInner: 'bg-light-grey rounded-3xl',
    handles: 'relative',
    handle:
      'absolute rounded-full bg-white border-racq-blue-active border-2 w-8 h-8 text-white mt-[-21px] ml-[-16px] cursor-pointer font-0 shadow-xl',
    fill: 'bg-racq-blue-active h-2.5 rounded-3xl max-w-full',
    marks: 'hidden',
  },
};
